import type { LogoVariant } from '@design-tokens/logos';

import LogoOnlyBlack from '../../assets/logos/Logo Only/Logo-Only-Black.svg';
import LogoOnlyGray from '../../assets/logos/Logo Only/Logo-Only-Gray.svg';
import LogoOnlyLightBlue from '../../assets/logos/Logo Only/Logo-Only-Light-Blue.svg';
import LogoOnlyPrimaryBlue from '../../assets/logos/Logo Only/Logo-Only-Primary-Blue.svg';
import LogoOnlyWhite from '../../assets/logos/Logo Only/Logo-Only-White.svg';
import LogoWithWordsBlack from '../../assets/logos/Logo with Words/Logo-with-Words-Black.svg';
import LogoWithWordsGray from '../../assets/logos/Logo with Words/Logo-with-Words-Gray.svg';
import LogoWithWordsLightBlue from '../../assets/logos/Logo with Words/Logo-with-Words-Light-Blue.svg';
import LogoWithWordsPrimaryBlue from '../../assets/logos/Logo with Words/Logo-with-Words-Primary-Blue.svg';
import LogoWithWordsWhite from '../../assets/logos/Logo with Words/Logo-with-Words-White.svg';

import { buildClassName } from '@utils';

import './Logo.css';

// Logo asset mapping
const logoAssets = {
  icon: {
    black: LogoOnlyBlack,
    gray: LogoOnlyGray,
    lightBlue: LogoOnlyLightBlue,
    primaryBlue: LogoOnlyPrimaryBlue,
    white: LogoOnlyWhite,
  },
  full: {
    black: LogoWithWordsBlack,
    gray: LogoWithWordsGray,
    lightBlue: LogoWithWordsLightBlue,
    primaryBlue: LogoWithWordsPrimaryBlue,
    white: LogoWithWordsWhite,
  },
};

export interface LogoProps {
  /**
   * Logo size
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether to show text with logo
   * @default false
   */
  showText?: boolean;
  /**
   * Logo color variant
   * @default 'primaryBlue'
   */
  variant?: LogoVariant;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Alt text for logo image
   */
  alt?: string;
}

export function Logo({
  size = 'medium',
  showText = false,
  variant = 'primaryBlue',
  className = '',
  onClick,
  alt = 'Bethlehem Homeschool Group Logo',
}: LogoProps) {
  const logoClasses = buildClassName(
    'bhg-logo',
    `bhg-logo--${size}`,
    showText && 'bhg-logo--with-text',
    onClick && 'bhg-logo--clickable',
    className
  );

  // Select the appropriate logo asset based on showText and variant
  const logoType = showText ? 'full' : 'icon';
  const logoSrc = logoAssets[logoType][variant];

  return (
    <div 
      className={logoClasses} 
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? alt : undefined}
    >
      <img 
        src={logoSrc} 
        alt={alt}
        className="bhg-logo-image"
      />
    </div>
  );
}
